import { put, takeLatest, call, select, take } from 'redux-saga/effects';
import { getAggr } from 'api';
import {
  AUTH_REFRESH_SUCCESS,
  AUTH_REFRESH_FAILURE,
  AUTH_REFRESH_SKIP,
  FETCH_UNITS_LIST_AGGR_REQUEST,
} from 'redux-api/action/actionTypes';
import {
  fetchUnitsListAggrSuccess,
  fetchUnitsListAggrFailure,
} from 'redux-api/action/unitsListAggr';
import { refreshRequest } from 'redux-api/action/auth';
import selectAuth from 'redux-api/reselect/auth';
import selectProfile from 'redux-api/reselect/profile';
import { openFeedback } from 'redux-api/action/feedback';
import translations from 'translations';
import adminParams from '../../lib/admin-params';
import stringifyNextPageParams from '../../lib/stringify-next-page-params';
import mapAggrBucketsToUnits from '../../lib/map-aggr-buckets-to-units';

function* buildFullAggrParams(querySuffix) {
  const { signInUserSession, adminRequestedStreetlibInternalId } = yield select(selectAuth);
  const { accessToken } = signInUserSession;
  const { isAdmin } = yield select(selectProfile);
  let params = `${
    adminRequestedStreetlibInternalId
      ? `adminRequestedStreetlibInternalId=${adminRequestedStreetlibInternalId}&`
      : ''
  }${querySuffix}`;
  params = `${isAdmin ? `adminRequestedAccess=true&` : ''}${params}`;
  if (isAdmin) params = adminParams(params);
  return { accessToken, params };
}

function* unitsListAggrSaga({ payload }) {
  const { sessionKey, targetPage, baseQuery, dataType } = payload;
  const { languageCode, currency } = yield select((s) => s.config);

  try {
    yield put(refreshRequest());
    const { type } = yield take([AUTH_REFRESH_SUCCESS, AUTH_REFRESH_FAILURE, AUTH_REFRESH_SKIP]);
    if (type === AUTH_REFRESH_FAILURE) throw new Error('401');

    const { pages, pageMeta, sessionKey: activeSession } = yield select((s) => s.unitsListAggr);
    if (activeSession !== sessionKey) {
      return;
    }

    if (pages[targetPage]) {
      yield put(fetchUnitsListAggrSuccess({ sessionKey, pages, pageMeta }));
      return;
    }

    const pageNums = Object.keys(pages).map(Number);
    const maxCached = pageNums.length ? Math.max(...pageNums) : 0;

    if (maxCached >= targetPage) {
      yield put(fetchUnitsListAggrSuccess({ sessionKey, pages, pageMeta }));
      return;
    }

    const mergedPages = { ...pages };
    const mergedMeta = { ...pageMeta };
    const startPage = maxCached > 0 ? maxCached + 1 : 1;

    for (let p = startPage; p <= targetPage; p += 1) {
      let querySuffix;
      if (p === 1) {
        querySuffix = baseQuery;
      } else {
        const prev = mergedMeta[p - 1];
        if (!prev?.nextPageParams) {
          yield put(
            fetchUnitsListAggrSuccess({
              sessionKey,
              pages: mergedPages,
              pageMeta: mergedMeta,
            }),
          );
          return;
        }
        querySuffix = stringifyNextPageParams(prev.nextPageParams);
      }

      const { accessToken, params } = yield call(buildFullAggrParams, querySuffix);
      const { data } = yield call(getAggr, accessToken, params);

      const skAfterFetch = yield select((s) => s.unitsListAggr.sessionKey);
      if (skAfterFetch !== sessionKey) {
        return;
      }

      const buckets = data?.aggregations?.results?.buckets || [];
      mergedPages[p] = mapAggrBucketsToUnits(buckets, {
        currency,
        languageCode,
        dataType,
      });
      mergedMeta[p] = {
        hasNext: !!data?.aggregations_after_key,
        nextPageParams: data?.next_page_params || null,
      };
    }

    yield put(
      fetchUnitsListAggrSuccess({
        sessionKey,
        pages: mergedPages,
        pageMeta: mergedMeta,
      }),
    );
  } catch (err) {
    yield put(
      openFeedback({
        message: translations[languageCode].generic_retrieve_api_error,
        error: err,
      }),
    );
    yield put(fetchUnitsListAggrFailure({ sessionKey, error: err }));
  }
}

export default function* watchUnitsListAggr() {
  yield takeLatest(FETCH_UNITS_LIST_AGGR_REQUEST, unitsListAggrSaga);
}
